﻿const axios = require('axios');
import mixin from './mixin.js';
import bulmaCollapsible from '@creativebulma/bulma-collapsible';
import FileUpload from './components/FileUpload.vue';
import ModalArchiveClaim from './components/modals/ArchiveClaim.vue';
import Tab from './components/tabs/tab.vue';
import Tabs from './components/tabs/tabs.vue';
import { required, requiredIf, email, integer, maxLength, minLength, helpers } from '@vuelidate/validators';
import { useVuelidate } from '@vuelidate/core';
import datePicker from 'vanillajs-datepicker/Datepicker';
import nl from 'vanillajs-datepicker/locales/nl';
Object.assign(datePicker.locales, nl);

const dutchPostal = helpers.regex(/^[1-9][0-9]{3} ?(?!sa|sd|ss|SA|SD|SS)[A-Za-z]{2}$/);
function validateIf(condition, rule) {
    return condition ? rule : {};
}

const today = new Date();
const yesterday = new Date(today.setDate(today.getDate() - 1));
const hundredYearsAgo = new Date();
hundredYearsAgo.setFullYear(hundredYearsAgo.getFullYear() - 100);
let maxdate = yesterday.toLocaleDateString('nl-NL', { year: 'numeric', month: 'short', day: '2-digit' });
let minbirthdate = hundredYearsAgo.toLocaleDateString('nl-NL', { year: 'numeric', month: 'short', day: '2-digit' });
const dutchMonths = new Map([...Array(12)].map((x, i) => [new Date(2022, i, 1, 0, 0, 0, 0).toLocaleDateString('nl-NL', { month: 'short' }).slice(0, 3), i]));
const birthOptions = {
    autohide: true,
    format: 'dd-M-yyyy',
    maxDate: maxdate,
    minDate: minbirthdate,
    language: 'nl',
    nextArrow: '>',
    prevArrow: '<',
    startView: 2,
    orientation: 'top'
}

const dateOptions = {
    autohide: true,
    format: 'dd-M-yyyy',
    maxDate: maxdate,
    language: 'nl',
    nextArrow: '>',
    prevArrow: '<',
    orientation: 'top'
}

const claimDetailApp = {
    setup: () => ({ v$: useVuelidate() }),
    mixins: [mixin],
    components: {
        FileUpload,
        ModalArchiveClaim,
        Tab,
        Tabs
    },
    data() {
        return {
            loading: false,
            claimLoading: true,
            hasClaim: false,
            allowClaim: true,
            allowArchive: true,
            claimID: '',
            claimEditable: false,
            claimArchived: false,
            header: null,
            menu: null,
            api: '',
            upload: '',
            claimantApi: '',
            referenceApi: '',
            claimantArray: [],
            redirectUrl: '/',
            step: 1,
            formData: {},
            hasError: false,
            recievedData: {
                typeAlias: ''
            },
            dataStep1: {
                typeAlias: ''
            },
            datepickers: [],
            defaultCustomerType: '',
            defaultTypeInvoiceAddress: '',
            defaultCountry: '',
            defaultFinalClaimStep: '',
            step1: {
                CustomerType: '',
                TypeInvoiceAddress: '',
                Claimant: '',
                ClientReference: '',
                Initials: '',
                MiddleName: '',
                LastName: '',
                Birthdate: '',
                ChamberOfCommerceNumberNP: '',
                BusinessName: '',
                ChamberOfCommerceNumberRP: '',
                Street: '',
                HouseNumber: '',
                HouseNumberAddition: '',
                Postalcode: '',
                City: '',
                Country: '',
                Phone: '',
                EmailAddress: '',
                BankAccount: '',
                InvoiceNumber: '',
                ClaimDescription: '',
                InvoiceAmount: '',
                InvoiceAmountDecimal: '',
                InvoiceDate: '',
                ClaimExpirationDate: '',
                AmountPaidToClaimant: '',
                DateOfAmountPaidToClaimant: '',
                AmountPaidToClaimantDecimal: '',
            },
            step2: {
                uploadFiles: [],
                filePaths: []
            },
            step3: {
                refInUse: false
            },
            step4: {
                FinalClaimStep: ''
            }
        }
    },
    validations() {
        return {
            step1: {
                CustomerType: { required },
                TypeInvoiceAddress: { required },
                Claimant: { required },
                ClientReference: { required },
                Initials: {
                    required: requiredIf(function () {
                        return this.step1.CustomerType === this.defaultCustomerType
                    })
                },
                LastName: {
                    required: requiredIf(function () {
                        return this.step1.CustomerType === this.defaultCustomerType
                    })
                },
                Birthdate: {
                    between(date) {
                        if (!date.length) return true;
                        let dateObj = this.getDateFromString(date);
                        return dateObj > hundredYearsAgo && dateObj < today
                    }
                },
                BusinessName: {
                    required: requiredIf(function () {
                        return this.step1.CustomerType !== this.defaultCustomerType
                    })
                },
                ChamberOfCommerceNumberRP: {
                    required: requiredIf(function () {
                        return this.step1.CustomerType !== this.defaultCustomerType
                    })
                },
                Street: { required },
                HouseNumber: { required },
                Postalcode: {
                    required,
                    ...validateIf(this.step1.TypeInvoiceAddress === this.defaultTypeInvoiceAddress, { dutchPostal })
                },
                City: { required },
                Country: {
                    required: requiredIf(function () {
                        return this.step1.TypeInvoiceAddress !== this.defaultTypeInvoiceAddress
                    })
                },
                EmailAddress: { email },
                InvoiceNumber: { required },
                InvoiceAmount: {
                    required,
                    integer,
                    aboveZero: function () {
                        return this.checkAmount(this.step1.InvoiceAmount, this.step1.InvoiceAmountDecimal)
                    }
                },
                InvoiceAmountDecimal: {
                    required: requiredIf(function () {
                        return this.step1.InvoiceAmount !== ''
                    }),
                    integer,
                    maxLength: maxLength(2),
                    minLength: minLength(2)
                },
                InvoiceDate: {
                    required,
                    maxValue(date) {
                        let dateObj = this.getDateFromString(date);
                        return dateObj < yesterday
                    }
                },
                ClaimExpirationDate: {
                    between(date) {
                        if (!date.length) return true;
                        if (!this.step1.InvoiceDate.length) return true;
                        let dateObj = this.getDateFromString(date);
                        let invoiceDateObj = this.getDateFromString(this.step1.InvoiceDate);
                        return dateObj < yesterday && dateObj > invoiceDateObj
                    }
                },
                AmountPaidToClaimant: {
                    integer,
                    between(val) {
                        if (!val.length) return true
                        return parseFloat(val + '.' + this.step1.AmountPaidToClaimantDecimal) <= parseFloat(this.step1.InvoiceAmount + '.' + this.step1.InvoiceAmountDecimal) && parseFloat(val + '.' + this.step1.AmountPaidToClaimantDecimal) > 0
                    }
                },
                AmountPaidToClaimantDecimal: {
                    required: requiredIf(function () {
                        return this.step1.AmountPaidToClaimant !== ''
                    }),
                    integer,
                    maxLength: maxLength(2),
                    minLength: minLength(2)
                },
                DateOfAmountPaidToClaimant: {
                    required: requiredIf(function () {
                        return this.step1.AmountPaidToClaimant !== '' && parseFloat(this.step1.AmountPaidToClaimant + '.' + this.step1.AmountPaidToClaimantDecimal) > 0
                    }),
                    between(date) {
                        if (!date.length) return true;
                        if (!this.step1.InvoiceDate.length) return true;
                        let dateObj = this.getDateFromString(date);
                        let invoiceDateObj = this.getDateFromString(this.step1.InvoiceDate);
                        return dateObj < yesterday && dateObj > invoiceDateObj
                    }
                }
            },
            step4: {
                FinalClaimStep: { required }
            }
        }
    },
    watch: {
        "step1.CustomerType": function (newVal, oldVal) {
            if (oldVal !== newVal) {
                if (newVal === this.defaultCustomerType) {
                    this.step1.BusinessName = '';
                    this.step1.ChamberOfCommerceNumberRP = '';
                } else {
                    this.step1.Initials = '';
                    this.step1.MiddleName = '';
                    this.step1.LastName = '';
                    this.step1.Birthdate = '';
                    this.step1.ChamberOfCommerceNumberNP = '';
                }
            }
        },
        "step1.InvoiceAmount": function (newVal, oldVal) {
            if (oldVal === '' && newVal !== '') {
                if (this.step1.InvoiceAmountDecimal === '') this.step1.InvoiceAmountDecimal = '00';
            }
            if (newVal === '') this.step1.InvoiceAmountDecimal = '';
        },
        "step1.AmountPaidToClaimant": function (newVal, oldVal) {
            if (oldVal === '' && newVal !== '') {
                if (this.step1.AmountPaidToClaimantDecimal === '') this.step1.AmountPaidToClaimantDecimal = '00';
            }
            if (newVal === '') this.step1.AmountPaidToClaimantDecimal = '';
        },
        "step1.TypeInvoiceAddress": function (val) {
            if (val === this.defaultTypeInvoiceAddress) {
                this.step1.Country = this.defaultCountry;
            } else {
                this.step1.Country = '';
            }
        }
    },
    methods: {
        getDateFromString(dateString) {
            if (!dateString.length) return new Date()
            const i = dateString.split('-');
            let dt = new Date(i[2], dutchMonths.get(i[1].slice(0, 3)), i[0]);
            return new Date(dt - dt.getTimezoneOffset() * 60000)
        },
        setDateRef(el) {
            if (el) {
                if (!this.datepickers.includes(el)) this.datepickers.push(el);
            }
        },
        getClaimantFromValue(val) {
            if (val === '') return '';
            let claimant = this.claimantArray.find(claimant => {
                return claimant.value === val
            });
            return claimant.name
        },
        initCalendars(calendars) {
            calendars.forEach(calendar => {
                let format = calendar.dataset.showformat;
                if (calendar.classList.contains('birthdate')) {
                    birthOptions.format = format;
                    new datePicker(calendar, birthOptions);
                } else {
                    dateOptions.format = format;
                    new datePicker(calendar, dateOptions);
                }
                calendar.addEventListener('changeDate', (e) => {
                    calendar.dispatchEvent(new Event('input', { bubbles: true }));
                });
            });
        },
        resetCalendars(calendars) {
            calendars.forEach(calendar => {
                calendar.datepicker.update();
            });
        },
        calcTotal() {
            if (this.step1.AmountPaidToClaimant === '') {
                return this.step1.InvoiceAmount + ',' + this.step1.InvoiceAmountDecimal
            } else {
                let invoiceAmount = parseFloat(this.step1.InvoiceAmount + '.' + this.step1.InvoiceAmountDecimal);
                let paidAmount = parseFloat(this.step1.AmountPaidToClaimant + '.' + this.step1.AmountPaidToClaimantDecimal);
                let total = invoiceAmount - paidAmount;
                return total.toFixed(2).toString().replace('.', ',')
            }
        },
        checkAmount(val1, val2) {
            return parseFloat(val1 + '.' + val2) > 0;
        },
        scrollTo($elm) {
            window.scrollTo({ top: $elm.getBoundingClientRect().top + window.pageYOffset - this.header.offsetHeight - this.menu.offsetHeight - 20, behavior: 'smooth' });
        },
        resetErrors(e) {
            this.v$.$reset();
        },
        async submitStep() {
            this.resetErrors(null);
            if (this.step === 1) {
                const valid1 = await this.v$.step1.$validate();
                if (!valid1) {
                    this.scrollTo(document.querySelector('.form-error').closest('.field'));
                } else {
                    this.loading = true;
                    this.formData = JSON.parse(JSON.stringify(this.step1));
                    if (this.step1.Birthdate !== '') this.formData.Birthdate = this.getDateFromString(this.step1.Birthdate).toISOString();
                    if (this.step1.InvoiceDate !== '') this.formData.InvoiceDate = this.getDateFromString(this.step1.InvoiceDate).toISOString();
                    if (this.step1.ClaimExpirationDate !== '') this.formData.ClaimExpirationDate = this.getDateFromString(this.step1.ClaimExpirationDate).toISOString();
                    if (this.step1.DateOfAmountPaidToClaimant !== '') this.formData.DateOfAmountPaidToClaimant = this.getDateFromString(this.step1.DateOfAmountPaidToClaimant).toISOString();
                    const checkref = await this.fetchData('', this.referenceApi + '?referentie=' + this.step1.ClientReference, 'get');
                    if (checkref.status === 200) {
                        this.step3.refInUse = checkref.data;
                    } else {
                        this.hasError = true;
                    }
                    this.loading = false;
                    this.step = 2;
                    this.$nextTick(() => {
                        this.scrollTo(this.$refs.steptop[1]);
                    });
                }
            } else if (this.step === 2) {
                this.step2.fileIDs = this.$refs.fileUploadClaimCreate.fileIDs;
                this.step2.uploadFiles = this.$refs.fileUploadClaimCreate.processedFiles;
                this.formData.FileIDs = this.step2.fileIDs;
                this.step = 3;
                this.$nextTick(() => {
                    this.scrollTo(this.$refs.steptop[2]);
                });
            } else if (this.step === 3) {
                this.step = 4;
                this.$nextTick(() => {
                    this.scrollTo(this.$refs.steptop[3]);
                });
            } else if (this.step === 4) {
                const valid4 = await this.v$.step4.$validate();
                if (!valid4) {
                    this.scrollTo(document.querySelector('.form-error').closest('.field'));
                } else {
                    if (this.step4.FinalClaimStep === this.defaultFinalClaimStep) {
                        location.href = this.redirectUrl;
                    } else if (this.step4.FinalClaimStep === 'varChoiceFinalStep_NewClaimOtherCustomer') {
                        Object.keys(this.step1).forEach(key => {
                            this.step1[key] = '';
                        });
                        this.step1.CustomerType = this.defaultCustomerType;
                        this.step1.TypeInvoiceAddress = this.defaultTypeInvoiceAddress;
                        this.step1.Country = this.defaultCountry;
                        this.step = 1;
                        this.$nextTick(() => {
                            this.scrollTo(this.$refs.steptop[0]);
                        });
                    } else {
                        this.step1.ClientReference = '';
                        this.step1.ClaimDescription = '';
                        this.step1.InvoiceAmount = '';
                        this.step1.InvoiceAmountDecimal = '';
                        this.step1.InvoiceDate = '';
                        this.step1.InvoiceNumber = '';
                        this.step1.ClaimExpirationDate = '';
                        this.step1.AmountPaidToClaimant = '';
                        this.step1.DateOfAmountPaidToClaimant = '';
                        this.step1.AmountPaidToClaimantDecimal = '';
                        this.step2.fileIDs = [];
                        this.step2.uploadFiles = [];
                        this.step = 1;
                        this.$nextTick(() => {
                            this.scrollTo(this.$refs.steptop[0]);
                        });
                    }
                }
            }
        },
        goBack(step) {
            this.step = step;
            this.$nextTick(() => {
                this.scrollTo(this.$refs.steptop[step - 1]);
            });
        },
        async submitForm() {
            this.loading = true;
            const response = await this.fetchData(this.formData, this.api + '/' + this.claimID, 'put');
            if (response.status === 200) {
                this.dataStep1 = response.data
                this.loading = false;
                if (this.dataStep1.typeAlias === 'Success') {
                    this.redirectUrl += '/' + this.dataStep1.claimID;
                    if (this.claimID !== '') this.claimID = this.dataStep1.claimID
                    const currentUrl = window.location.href;
                    const urlBase = currentUrl.split('/');
                    urlBase.pop();
                    window.history.replaceState({}, '', urlBase.join("/") + '/' + this.dataStep1.claimID);
                    this.step = 1;
                    this.$refs.tab2.isActive = false;
                    this.$refs.tab1.isActive = true;
                } else {
                    let text = "Er is iets mis gegaan!\nControleer het formulier en probeer opnieuw";
                    if (confirm(text) == true) {
                        this.resetErrors(null);
                        this.step = 1;
                        this.$nextTick(() => {
                            this.scrollTo(document.querySelector('.form-error'));
                        });
                    }
                }
            } else {
                this.hasError = true;
            }
        },
        async fetchData(formData, apiUrl, methodType) {
            try {
                const response = await axios({
                    method: methodType,
                    url: apiUrl,
                    data: formData,
                    headers: { "Content-Type": "application/json" },
                })
                return response;
            } catch (error) {
                console.log(error);
            }
        },
        async getClaimants() {
            if (sessionStorage.getItem("user") !== null) {
                this.claimantArray = JSON.parse(sessionStorage.getItem("user")).organisationSoortZaakCodes;
            } else {
                const response = await this.fetchData({}, this.claimantApi, 'get');
                if (response.status === 200) {
                    this.claimantArray = response.data;
                }
            }
        },
        assignData() {
            Object.keys(this.step1).forEach(key => {
                const lowerKey = key.charAt(0).toLowerCase() + key.slice(1);
                this.step1[key] = this.recievedData[lowerKey];
            });
            if (this.step1.Birthdate !== null && this.step1.Birthdate !== '') this.step1.Birthdate = new Date(this.step1.Birthdate).toLocaleDateString('nl-NL', { year: 'numeric', month: 'short', day: '2-digit' }).replaceAll(' ', '-');
            if (this.step1.InvoiceDate !== null && this.step1.InvoiceDate !== '') this.step1.InvoiceDate = new Date(this.step1.InvoiceDate).toLocaleDateString('nl-NL', { year: 'numeric', month: 'short', day: '2-digit' }).replaceAll(' ', '-');
            if (this.step1.ClaimExpirationDate !== null && this.step1.ClaimExpirationDate !== '') this.step1.ClaimExpirationDate = new Date(this.step1.ClaimExpirationDate).toLocaleDateString('nl-NL', { year: 'numeric', month: 'short', day: '2-digit' }).replaceAll(' ', '-');
            if (this.step1.DateOfAmountPaidToClaimant !== null && this.step1.DateOfAmountPaidToClaimant !== '') this.step1.DateOfAmountPaidToClaimant = new Date(this.step1.DateOfAmountPaidToClaimant).toLocaleDateString('nl-NL', { year: 'numeric', month: 'short', day: '2-digit' }).replaceAll(' ', '-');
            Object.keys(this.step2).forEach(key => {
                if (this.recievedData[key] !== null) this.step2[key] = this.recievedData[key];
            });
            this.claimArchived = this.recievedData['claimArchived'];
            this.claimEditable = this.recievedData['allowEdit'];
            this.allowArchive = this.recievedData['allowArchive'];
            this.step2.fileIDs = this.recievedData['fileIDs'];
            this.step2.processedFiles = this.recievedData['linkedFiles'];
            if (this.claimEditable) {
                this.$nextTick(() => {
                    this.$refs.fileUploadClaimCreate.fileIDs = this.step2.fileIDs;
                    this.$refs.fileUploadClaimCreate.processedFiles = this.step2.processedFiles;
                });
            }
        },
        async archiveClaim() {
            const apiUrl = this.api + '/' + this.claimID + '/archive';
            const response = await this.fetchData({}, apiUrl, 'put');
            if (response.status === 200) {
                console.log(response)
                this.claimArchived = true;
            }
        },
        openModal(modalName, props = {}) {
            if (!modalName) return;
            const component = this.$options.components[modalName];

            if (component) {
                this.$store.commit('openModal',
                    {
                        component: component,
                        props: props,
                    }
                );
            }
        }
    },
    created() {
        if (sessionStorage.getItem("user") !== null) {
            this.allowClaim = JSON.parse(sessionStorage.getItem("user")).organisationAllowNewClaims;
        }
    },
    async mounted() {
        this.header = document.getElementById('navbar');
        this.menu = document.getElementById('menu');
        if (this.$el.dataset.api) this.api = this.$el.dataset.api;
        if (this.$el.dataset.upload) this.upload = this.$el.dataset.upload;
        if (this.$el.dataset.claimant) this.claimantApi = this.$el.dataset.claimant;
        if (this.$el.dataset.reference) this.referenceApi = this.$el.dataset.reference;
        if (this.$el.dataset.redirecturltemplate) this.redirectUrl = this.$el.dataset.redirecturltemplate;
        if (this.$el.dataset.defaultcountrycode) {
            this.defaultCountry = this.$el.dataset.defaultcountrycode;
            this.step1.Country = this.defaultCountry;
        }
        if (this.$el.dataset.defaultcustomertype) {
            this.defaultCustomerType = this.$el.dataset.defaultcustomertype;
            this.step1.CustomerType = this.defaultCustomerType;
        }
        if (this.$el.dataset.defaulttypeinvoiceaddress) {
            this.defaultTypeInvoiceAddress = this.$el.dataset.defaulttypeinvoiceaddress;
            this.step1.TypeInvoiceAddress = this.defaultTypeInvoiceAddress;
        }
        if (this.$el.dataset.defaultfinalclaimstep) {
            this.defaultFinalClaimStep = this.$el.dataset.defaultfinalclaimstep;
            this.step4.FinalClaimStep = this.defaultFinalClaimStep;
        }
        if (this.$el.dataset.claimid) {
            this.claimID = this.$el.dataset.claimid;
            this.hasClaim = true;
        }
        if (this.api !== '' && this.claimID !== '') {
            let response = await this.fetchData({}, `${this.api}/${this.claimID}`, 'GET');
            if (response.status === 200) {
                this.recievedData = response.data;
                this.assignData();
                this.claimLoading = false;
                this.getClaimants();
                this.$nextTick(() => {
                    bulmaCollapsible.attach('.is-collapsible');
                    this.initCalendars(this.datepickers);
                });
            }
        }
        this.$nextTick(() => {
            this.$watch(
                () => {
                    return this.$refs.tab1.isActive
                },
                async (val) => {
                    if (val) {
                        if (this.api !== '' && this.claimID !== '') {
                            this.claimLoading = true;
                            let response = await this.fetchData({}, `${this.api}/${this.claimID}`, 'GET');
                            if (response.status === 200) {
                                this.recievedData = response.data;
                                this.assignData();
                                this.claimLoading = false;
                            }
                        }
                    }
                }
            );
        });
    }
}

export default claimDetailApp;
